import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import './mobile.css';

const API_URL = 'https://solveure-production.up.railway.app/api';

const C = {
  navy: '#0B1F3A', gold: '#C9A84C', cream: '#F5F0E8',
  cardBg: '#FDFAF5', border: '#D4C5A9', slate: '#4A5568',
  green: '#2D6A4F', red: '#C0392B', white: '#FFFFFF', lgray: '#F0EDE8',
};

const S = {
  page: { minHeight: '100vh', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Lato, sans-serif', padding: 20 },
  card: { background: C.cardBg, borderRadius: 12, padding: 40, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  error: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 16, color: C.red, fontSize: 13 },
  success: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12, marginBottom: 16, color: C.green, fontSize: 13 },
  label: { fontSize: 11, fontWeight: 700, color: C.slate, letterSpacing: '0.08em', display: 'block', marginBottom: 5 },
  input: { display: 'block', width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', fontSize: 15, boxSizing: 'border-box', marginBottom: 14, background: C.white },
  btn: { background: C.navy, color: C.gold, border: 'none', borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' },
  btnFull: { width: '100%', background: C.navy, color: C.gold, border: 'none', borderRadius: 10, padding: '14px 0', cursor: 'pointer', fontWeight: 700, fontSize: 15, fontFamily: 'inherit', marginTop: 8 },
  btnGold: { background: C.gold, color: C.navy, border: 'none', borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' },
  btnRed: { background: C.red, color: C.white, border: 'none', borderRadius: 8, padding: '12px 24px', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' },
  link: { background: 'none', border: 'none', color: C.gold, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textDecoration: 'underline' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  section: { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, paddingBottom: 28, marginBottom: 20 },
  sectionTitle: { fontFamily: 'Georgia, serif', fontSize: 17, fontWeight: 700, color: C.navy, margin: '0 0 20px', paddingBottom: 12, borderBottom: `2px solid ${C.gold}` },
  select: { display: 'block', width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', fontSize: 15, boxSizing: 'border-box', marginBottom: 14, background: C.white },
};

const ROLE_CONFIG = {
  admin:      { label: 'Administrator',    icon: '⚙️',  color: '#7B2D8B' },
  owner:      { label: 'Property Owner',   icon: '🏛️', color: C.gold },
  tenant:     { label: 'Tenant',           icon: '🏠',  color: '#2980B9' },
  lawyer:     { label: 'Legal Counsel',    icon: '⚖️',  color: C.green },
  accountant: { label: 'Accountant',       icon: '📊',  color: '#E67E22' },
  manager:    { label: 'Property Manager', icon: '🔑',  color: '#8E44AD' },
};

const TICKET_CATEGORIES = ['Legal', 'Finance', 'Maintenance', 'Immigration', 'Payment', 'Document', 'Other'];
const TICKET_PRIORITIES = ['Low', 'Medium', 'High'];

function Logo({ invert = false, size = 180 }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <img src="/logo.jpg" alt="SOLVEURE" style={{
        maxWidth: size, marginBottom: 4,
        mixBlendMode: invert ? 'normal' : 'multiply',
        filter: invert ? 'brightness(0) invert(1)' : 'none',
        opacity: invert ? 0.85 : 1,
      }} />
      {!invert && <div style={{ fontSize: 11, color: C.gold, letterSpacing: 3, textTransform: 'uppercase' }}>Property Management Solutions</div>}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', placeholder = '', disabled = false, hint = '' }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      <input style={{ ...S.input, background: disabled ? C.lgray : C.white, color: disabled ? C.slate : '#000' }}
        type={type} value={value || ''} onChange={e => onChange && onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled} />
      {hint && <div style={{ fontSize: 11, color: C.slate, marginTop: -10, marginBottom: 12 }}>{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder = 'Select...', disabled = false }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      <select style={{ ...S.select, background: disabled ? C.lgray : C.white }} value={value || ''} onChange={e => onChange && onChange(e.target.value)} disabled={disabled}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function FileUpload({ label, fileName, onChange, disabled = false }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={S.label}>{label}</label>
      {fileName && <div style={{ fontSize: 12, color: C.green, marginBottom: 6 }}>✓ {fileName}</div>}
      {!disabled && (
        <label style={{ display: 'inline-block', background: C.navy, color: C.gold, borderRadius: 6, padding: '8px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
          Upload PDF
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
            onChange={e => e.target.files[0] && onChange(e.target.files[0].name)} />
        </label>
      )}
    </div>
  );
}

function Badge({ role }) {
  const rc = ROLE_CONFIG[role] || { label: role, icon: '👤', color: C.slate };
  return (
    <span style={{ background: rc.color + '22', color: rc.color, border: `1px solid ${rc.color}44`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {rc.icon} {rc.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const colors = { Open: C.red, 'In Progress': '#E67E22', Resolved: C.green, Closed: C.slate };
  const color = colors[status] || C.slate;
  return (
    <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
      {status}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const colors = { High: C.red, Medium: '#E67E22', Low: C.green };
  const color = colors[priority] || C.slate;
  return (
    <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
      {priority}
    </span>
  );
}

// ── Top Bar ───────────────────────────────────────────────────────────────────
function TopBar({ user, onLogout, onMenuToggle, menuOpen }) {
  const navigate = useNavigate();
  const logout = () => { localStorage.removeItem('user'); localStorage.removeItem('token'); onLogout(); navigate('/login'); };

  return (
    <div style={{ background: C.navy, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, position: 'sticky', top: 0, zIndex: 200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onMenuToggle} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={{ display: 'block', width: 22, height: 2, background: menuOpen ? C.gold : '#ffffff99', borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ display: 'block', width: 22, height: 2, background: menuOpen ? 'transparent' : '#ffffff99', borderRadius: 2 }} />
          <span style={{ display: 'block', width: 22, height: 2, background: menuOpen ? C.gold : '#ffffff99', borderRadius: 2, transition: 'all 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
        <span style={{ fontFamily: 'Georgia, serif', fontSize: 18, fontWeight: 700, letterSpacing: 4, color: C.gold }}>SOLVEURE</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#ffffff88', fontSize: 12, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name.split(' ')[0]}</span>
        <button onClick={logout} style={{ background: 'transparent', border: `1px solid ${C.gold}44`, color: C.gold, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Sign Out</button>
      </div>
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ active, onNavigate, role, isOpen, onClose }) {
  const items = [
    { key: 'dashboard', icon: '◈', label: 'Dashboard', roles: ['admin','owner','tenant','lawyer','accountant','manager'] },
    { key: 'property', icon: '🏛️', label: 'Property Details', roles: ['owner','admin','manager'] },
    { key: 'legal', icon: '⚖️', label: 'Legal Details', roles: ['owner','admin','manager','lawyer'] },
    { key: 'permit', icon: '🛂', label: 'Residence Permit', roles: ['owner','admin','manager','lawyer'] },
    { key: 'accounting', icon: '📊', label: 'Accounting', roles: ['owner','admin','manager','accountant'] },
    { key: 'bills', icon: '💡', label: 'Bills Details', roles: ['owner','admin','manager'] },
    { key: 'tenant', icon: '🏠', label: 'Tenant Info', roles: ['owner','admin','manager'] },
    { key: 'bank', icon: '🏦', label: 'Bank Details', roles: ['owner','admin','manager'] },
    { key: 'tickets', icon: '🎫', label: 'Tickets', roles: ['admin','owner','tenant','lawyer','accountant','manager'] },
    { key: 'users', icon: '👥', label: 'User Management', roles: ['admin'] },
  ].filter(i => i.roles.includes(role));

  return (
    <>
      {isOpen && window.innerWidth <= 768 && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 150, cursor: 'pointer' }} />}
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 260, background: '#0d2340', zIndex: 160, padding: '16px 10px', transform: (isOpen || window.innerWidth > 768) ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', overflowY: 'auto', boxShadow: isOpen ? '4px 0 20px rgba(0,0,0,0.4)' : 'none' }}>
        <div style={{ padding: '8px 12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <img src="/logo.jpg" alt="SOLVEURE" style={{ maxWidth: 120, filter: 'brightness(0) invert(1)', opacity: 0.8 }} />
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#ffffff88', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>
        {items.map(n => (
          <button key={n.key} onClick={() => { onNavigate(n.key); onClose(); }} style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: active === n.key ? `${C.gold}22` : 'transparent',
            color: active === n.key ? C.gold : '#ffffff88',
            border: active === n.key ? `1px solid ${C.gold}33` : '1px solid transparent',
            borderRadius: 8, padding: '11px 14px', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 14, fontWeight: active === n.key ? 600 : 400, marginBottom: 4,
          }}>{n.icon} &nbsp;{n.label}</button>
        ))}
      </div>
    </>
  );
}

// ── Tickets Panel ─────────────────────────────────────────────────────────────
function TicketsPanel({ user, token }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [form, setForm] = useState({ title: '', description: '', category: 'Other', priority: 'Medium' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/tickets`, { headers });
      const data = await res.json();
      setTickets(Array.isArray(data) ? data : []);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadTickets(); }, []);

  const loadTicket = async (id) => {
    try {
      const res = await fetch(`${API_URL}/tickets/${id}`, { headers });
      const data = await res.json();
      setSelectedTicket(data);
    } catch {}
  };

  const submitTicket = async () => {
    setError(''); setSuccess('');
    if (!form.title.trim()) return setError('Please enter a title.');
    if (!form.description.trim()) return setError('Please enter a description.');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/tickets`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...form, category: form.category.toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Failed to submit ticket.');
      else {
        setSuccess('✓ Ticket submitted! Email notifications have been sent.');
        setForm({ title: '', description: '', category: 'Other', priority: 'Medium' });
        setShowForm(false);
        loadTickets();
      }
    } catch { setError('Could not connect to server.'); }
    finally { setSubmitting(false); }
  };

  const submitComment = async () => {
    if (!newComment.trim()) return;
    try {
      await fetch(`${API_URL}/tickets/${selectedTicket.id}/comments`, {
        method: 'POST', headers,
        body: JSON.stringify({ body: newComment }),
      });
      setNewComment('');
      loadTicket(selectedTicket.id);
      loadTickets();
    } catch {}
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/tickets/${id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ status }),
      });
      if (selectedTicket?.id === id) loadTicket(id);
      loadTickets();
    } catch {}
  };

  if (selectedTicket) return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={() => setSelectedTicket(null)} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: C.navy }}>← Back</button>
        <h2 style={{ fontFamily: 'Georgia, serif', color: C.navy, fontSize: 18, margin: 0, flex: 1 }}>{selectedTicket.ticket_number}</h2>
      </div>

      <div style={S.section}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <StatusBadge status={selectedTicket.status?.replace('_', ' ')?.replace(/\b\w/g, c => c.toUpperCase())} />
          <PriorityBadge priority={selectedTicket.priority?.replace(/\b\w/g, c => c.toUpperCase())} />
          <span style={{ fontSize: 12, color: C.slate, padding: '2px 8px', background: C.lgray, borderRadius: 20 }}>{selectedTicket.category}</span>
        </div>
        <h3 style={{ color: C.navy, margin: '0 0 8px', fontSize: 16 }}>{selectedTicket.title}</h3>
        <p style={{ color: C.slate, fontSize: 14, lineHeight: 1.7, margin: '0 0 12px' }}>{selectedTicket.description}</p>
        <div style={{ fontSize: 12, color: '#9CA3AF' }}>By {selectedTicket.author_name} · {selectedTicket.author_role} · {new Date(selectedTicket.created_at).toLocaleDateString()}</div>

        {['admin', 'manager'].includes(user.role) && (
          <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['in_progress', 'resolved', 'closed'].map(s => (
              <button key={s} onClick={() => updateStatus(selectedTicket.id, s)}
                style={{ background: C.lgray, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', color: C.navy }}>
                Mark {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Comments */}
      <div style={S.section}>
        <div style={S.sectionTitle}>💬 Comments</div>
        {selectedTicket.comments?.length > 0 ? (
          selectedTicket.comments.map((c, i) => (
            <div key={i} style={{ padding: '12px 0', borderBottom: i < selectedTicket.comments.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: 700, color: C.navy, fontSize: 13 }}>{c.author_name}</span>
                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p style={{ color: C.slate, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{c.body}</p>
            </div>
          ))
        ) : (
          <p style={{ color: '#9CA3AF', fontSize: 13 }}>No comments yet.</p>
        )}

        <div style={{ marginTop: 16 }}>
          <textarea value={newComment} onChange={e => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', fontSize: 14, resize: 'vertical', minHeight: 80, boxSizing: 'border-box', marginBottom: 10 }} />
          <button style={S.btn} onClick={submitComment}>Send Comment</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia, serif', color: C.navy, fontSize: 20, margin: 0 }}>🎫 Tickets</h2>
          <p style={{ color: C.slate, fontSize: 13, margin: '4px 0 0' }}>{tickets.length} ticket{tickets.length !== 1 ? 's' : ''}</p>
        </div>
        <button style={S.btnGold} onClick={() => { setShowForm(!showForm); setError(''); setSuccess(''); }}>
          {showForm ? '✕ Cancel' : '+ Open New Ticket'}
        </button>
      </div>

      {success && <div style={S.success}>{success}</div>}

      {showForm && (
        <div style={{ ...S.section, borderLeft: `4px solid ${C.gold}`, marginBottom: 24 }}>
          <div style={S.sectionTitle}>📝 Open a New Ticket</div>
          {error && <div style={S.error}>{error}</div>}
          <Field label="TITLE" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="Brief description of the issue" />
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>DESCRIPTION</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe your issue in detail..."
              style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', fontSize: 14, resize: 'vertical', minHeight: 100, boxSizing: 'border-box' }} />
          </div>
          <div style={S.row2}>
            <SelectField label="CATEGORY" value={form.category} onChange={v => setForm(f => ({ ...f, category: v }))}
              options={TICKET_CATEGORIES.map(c => ({ value: c, label: c }))} />
            <SelectField label="PRIORITY" value={form.priority} onChange={v => setForm(f => ({ ...f, priority: v }))}
              options={TICKET_PRIORITIES.map(p => ({ value: p, label: p }))} />
          </div>
          <div style={{ background: C.lgray, borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, color: C.slate, lineHeight: 1.7 }}>
            📧 Email notifications will be sent automatically to all relevant parties when you submit this ticket.
          </div>
          <button style={S.btnGold} onClick={submitTicket} disabled={submitting}>
            {submitting ? 'Submitting...' : '🎫 Submit Ticket →'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: C.slate, padding: 40 }}>Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.slate, padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎫</div>
          <div style={{ fontWeight: 600, color: C.navy }}>No tickets yet</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Open a new ticket to get help from our team.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tickets.map(t => (
            <div key={t.id} onClick={() => loadTicket(t.id)} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px', cursor: 'pointer', borderLeft: `4px solid ${t.priority === 'high' ? C.red : t.priority === 'medium' ? '#E67E22' : C.green}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: C.slate }}>{t.ticket_number}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <StatusBadge status={t.status?.replace('_', ' ')?.replace(/\b\w/g, c => c.toUpperCase())} />
                  <PriorityBadge priority={t.priority?.replace(/\b\w/g, c => c.toUpperCase())} />
                </div>
              </div>
              <div style={{ fontWeight: 700, color: C.navy, fontSize: 14, marginBottom: 4 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: C.slate }}>
                {t.author_name} · {t.category} · {new Date(t.created_at).toLocaleDateString()}
                {t.comment_count > 0 && <span style={{ marginLeft: 8 }}>💬 {t.comment_count}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Owner Sections ────────────────────────────────────────────────────────────
function OwnerSections({ active, data, setData, canEdit, lawyers, accountants }) {
  const set = f => v => setData(d => ({ ...d, [f]: v }));

  if (active === 'property') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>🏛️ Property Details</div>
      <Field label="PROPERTY ADDRESS" value={data.property_address} onChange={set('property_address')} placeholder="15 Ermou Street, Athens" disabled={!canEdit} />
      <div style={S.row2}>
        <Field label="POSTAL CODE" value={data.postal_code} onChange={set('postal_code')} placeholder="10563" disabled={!canEdit} />
        <Field label="FLOOR" value={data.floor} onChange={set('floor')} placeholder="3rd" disabled={!canEdit} />
      </div>
      <Field label="APARTMENT NUMBER" value={data.apartment_number} onChange={set('apartment_number')} placeholder="Apartment 3A" disabled={!canEdit} />
    </div>
  );

  if (active === 'legal') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>⚖️ Legal Details</div>
      <Field label="PURCHASE DATE" value={data.purchase_date} onChange={set('purchase_date')} type="date" disabled={!canEdit} />
      <Field label="PURCHASE PRICE (€)" value={data.purchase_price} onChange={set('purchase_price')} placeholder="e.g. 250000" type="number" disabled={!canEdit} />
      <FileUpload label="PURCHASE CONTRACT (PDF)" fileName={data.purchase_contract_name} onChange={set('purchase_contract_name')} disabled={!canEdit} />
      <FileUpload label="CERTIFICATION (PDF)" fileName={data.certification_name} onChange={set('certification_name')} disabled={!canEdit} />
      <FileUpload label="COPY OF RESIDENCE PERMIT CARDS (PDF)" fileName={data.permit_cards_name} onChange={set('permit_cards_name')} disabled={!canEdit} />
      <FileUpload label="TITLE DEED (PDF)" fileName={data.title_deed_name} onChange={set('title_deed_name')} disabled={!canEdit} />
      <FileUpload label="PASSPORT COPIES (PDF)" fileName={data.passport_copies_name} onChange={set('passport_copies_name')} disabled={!canEdit} />
      <SelectField label="LAWYER" value={data.lawyer_id} onChange={set('lawyer_id')} disabled={!canEdit}
        placeholder={lawyers.length ? 'Select assigned lawyer...' : 'No lawyers registered yet'}
        options={lawyers.map(l => ({ value: String(l.id), label: `${l.name} — ${l.email}` }))} />
    </div>
  );

  if (active === 'permit') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>🛂 Residence Permit</div>
      <Field label="PERMIT EXPIRY DATE" value={data.permit_expiry_date} onChange={set('permit_expiry_date')} type="date" disabled={!canEdit} />
      <Field label="RENEWAL REMINDER DATE" value={data.permit_renewal_date} onChange={() => {}} type="date" disabled={true}
        hint="Auto-set to 2 months before expiry. Reminder emails sent automatically." />
      {data.permit_renewal_date && (
        <div style={{ background: '#FFF9E6', border: `1px solid ${C.gold}`, borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 13, color: C.navy, fontWeight: 700, marginBottom: 6 }}>⏰ Reminder on {data.permit_renewal_date}</div>
          <div style={{ fontSize: 12, color: C.slate, lineHeight: 1.8 }}>Emails sent to: Owner • Admin • Property Manager • Lawyer</div>
        </div>
      )}
    </div>
  );

  if (active === 'accounting') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>📊 Accounting Details</div>
      <SelectField label="ACCOUNTANT" value={data.accountant_id} onChange={set('accountant_id')} disabled={!canEdit}
        placeholder={accountants.length ? 'Select accountant...' : 'No accountants registered yet'}
        options={accountants.map(a => ({ value: String(a.id), label: `${a.name} — ${a.email}` }))} />

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>TAX DECLARATIONS</div>
        {['2023', '2024', '2025', '2026', '2027'].map(year => (
          <div key={year} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.lgray, borderRadius: 8, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: C.navy, width: 44, fontSize: 14 }}>{year}</div>
            <div style={{ flex: 1, fontSize: 12 }}>
              {data[`tax_${year}`] ? <span style={{ color: C.green, fontWeight: 600 }}>✓ {data[`tax_${year}`]}</span> : <span style={{ color: '#9CA3AF' }}>Not uploaded</span>}
            </div>
            {canEdit && (
              <label style={{ background: C.navy, color: C.gold, borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                Upload <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files[0] && set(`tax_${year}`)(e.target.files[0].name)} />
              </label>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 10, borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>ENFIA DECLARATIONS</div>
        {['2022', '2023', '2024', '2025', '2026', '2027'].map(year => (
          <div key={year} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: C.lgray, borderRadius: 8, marginBottom: 8 }}>
            <div style={{ fontWeight: 700, color: C.navy, width: 44, fontSize: 14 }}>{year}</div>
            <div style={{ flex: 1, fontSize: 12 }}>
              {data[`enfia_${year}`] ? <span style={{ color: C.green, fontWeight: 600 }}>✓ {data[`enfia_${year}`]}</span> : <span style={{ color: '#9CA3AF' }}>Not uploaded</span>}
            </div>
            {canEdit && (
              <label style={{ background: C.navy, color: C.gold, borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                Upload <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => e.target.files[0] && set(`enfia_${year}`)(e.target.files[0].name)} />
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  if (active === 'bills') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>💡 Bills Details</div>
      <Field label="ELECTRICITY METER NUMBER" value={data.electricity_meter} onChange={set('electricity_meter')} placeholder="1234567890" disabled={!canEdit} />
      <Field label="WATER METER NUMBER" value={data.water_meter} onChange={set('water_meter')} placeholder="0987654321" disabled={!canEdit} />
      <Field label="GAS METER NUMBER" value={data.gas_meter} onChange={set('gas_meter')} placeholder="1122334455" disabled={!canEdit} />
      <Field label="BUILDING MANAGER NAME" value={data.building_manager} onChange={set('building_manager')} placeholder="Full name" disabled={!canEdit} />
      <Field label="BUILDING MANAGER PHONE" value={data.building_manager_phone} onChange={set('building_manager_phone')} placeholder="+30 210 123 4567" type="tel" disabled={!canEdit} />
      <Field label="BUILDING MANAGER EMAIL" value={data.building_manager_email} onChange={set('building_manager_email')} placeholder="manager@building.com" type="email" disabled={!canEdit} />
    </div>
  );

  if (active === 'tenant') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>🏠 Tenant Information</div>
      <Field label="CURRENT TENANT" value={data.current_tenant} onChange={set('current_tenant')} placeholder="Full name" disabled={!canEdit} />
      <Field label="TENANT PHONE NUMBER" value={data.tenant_phone} onChange={set('tenant_phone')} placeholder="+30 210 123 4567" type="tel" disabled={!canEdit} />
      <Field label="RENTAL CONTRACT EXPIRY" value={data.rental_expiry} onChange={set('rental_expiry')} type="date" disabled={!canEdit} />
      <Field label="RENTAL PRICE (€/month)" value={data.rental_price} onChange={set('rental_price')} placeholder="e.g. 1500" type="number" disabled={!canEdit} />
      <Field label="DURATION OF CONTRACT" value={data.rental_duration} onChange={set('rental_duration')} placeholder="e.g. 12 months" disabled={!canEdit} />
      <FileUpload label="RENTAL CONTRACT (PDF)" fileName={data.rental_contract_name} onChange={set('rental_contract_name')} disabled={!canEdit} />
    </div>
  );

  if (active === 'bank') return (
    <div style={S.section}>
      <div style={S.sectionTitle}>🏦 Bank Account Details</div>
      <div style={{ background: '#FFF9E6', border: `1px solid ${C.gold}55`, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: C.slate }}>
        🔒 Only visible to you, Admin and Property Manager.
      </div>
      <Field label="BANK" value={data.bank_name} onChange={set('bank_name')} placeholder="National Bank of Greece" disabled={!canEdit} />
      <Field label="BRANCH" value={data.bank_branch} onChange={set('bank_branch')} placeholder="Athens Central Branch" disabled={!canEdit} />
      <Field label="IBAN" value={data.iban} onChange={set('iban')} placeholder="GR16 0110 1250 0000 0001 2300 695" disabled={!canEdit} />
      <Field label="SWIFT CODE" value={data.swift_code} onChange={set('swift_code')} placeholder="ETHNGRAA" disabled={!canEdit} />
    </div>
  );

  return null;
}

// ── User Management ───────────────────────────────────────────────────────────
function UserManagementPanel({ token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ first_name: '', last_name: '', email: '', phone: '', role: 'owner' });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, []);
  const setInv = f => v => setInvite(i => ({ ...i, [f]: v }));

  const sendInvite = async () => {
    setInviteError(''); setInviteSuccess('');
    if (!invite.first_name || !invite.last_name) return setInviteError('Please enter first and last name.');
    if (!invite.email) return setInviteError('Please enter an email address.');
    setInviting(true);
    try {
      const res = await fetch(`${API_URL}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...invite, name: `${invite.first_name} ${invite.last_name}` }),
      });
      const data = await res.json();
      if (!res.ok) setInviteError(data.error || 'Invitation failed.');
      else { setInviteSuccess(`✓ Invitation sent to ${invite.email}.`); setInvite({ first_name: '', last_name: '', email: '', phone: '', role: 'owner' }); setShowInvite(false); loadUsers(); }
    } catch { setInviteError('Could not connect to server.'); }
    finally { setInviting(false); }
  };

  const deactivateUser = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}?`)) return;
    try { await fetch(`${API_URL}/users/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); loadUsers(); }
    catch { alert('Could not deactivate user.'); }
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia, serif', color: C.navy, fontSize: 20, margin: 0 }}>👥 User Management</h2>
          <p style={{ color: C.slate, fontSize: 13, margin: '4px 0 0' }}>{users.length} users registered</p>
        </div>
        <button style={S.btnGold} onClick={() => { setShowInvite(!showInvite); setInviteError(''); setInviteSuccess(''); }}>
          {showInvite ? '✕ Cancel' : '+ Invite User'}
        </button>
      </div>

      {showInvite && (
        <div style={{ ...S.section, borderLeft: `4px solid ${C.gold}`, marginBottom: 24 }}>
          <div style={S.sectionTitle}>📧 Invite New User</div>
          {inviteError && <div style={S.error}>{inviteError}</div>}
          {inviteSuccess && <div style={S.success}>{inviteSuccess}</div>}
          <div style={S.row2}>
            <Field label="FIRST NAME" value={invite.first_name} onChange={setInv('first_name')} placeholder="John" />
            <Field label="LAST NAME" value={invite.last_name} onChange={setInv('last_name')} placeholder="Smith" />
          </div>
          <Field label="EMAIL ADDRESS" value={invite.email} onChange={setInv('email')} placeholder="john@example.com" type="email" />
          <Field label="PHONE (OPTIONAL)" value={invite.phone} onChange={setInv('phone')} placeholder="+30 210 123 4567" type="tel" />
          <SelectField label="ROLE" value={invite.role} onChange={setInv('role')}
            options={Object.entries(ROLE_CONFIG).map(([k, v]) => ({ value: k, label: `${v.icon} ${v.label}` }))} />
          <div style={{ background: C.lgray, borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12, color: C.slate, lineHeight: 1.7 }}>
            A welcome email with login credentials will be sent automatically.
          </div>
          <button style={S.btnGold} onClick={sendInvite} disabled={inviting}>{inviting ? 'Sending...' : '📧 Send Invitation →'}</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input style={{ ...S.input, marginBottom: 0, flex: 1, minWidth: 180 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...S.select, marginBottom: 0, minWidth: 140 }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">All roles</option>
          {Object.entries(ROLE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
      </div>

      {loading ? <div style={{ textAlign: 'center', color: C.slate, padding: 40 }}>Loading...</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(u => {
            const rc = ROLE_CONFIG[u.role] || { icon: '👤', color: C.slate };
            return (
              <div key={u.id} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: rc.color + '22', border: `2px solid ${rc.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{rc.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: C.navy, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize: 12, color: C.slate, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge role={u.role} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: u.is_active !== false ? C.green : C.red }}>{u.is_active !== false ? '● Active' : '● Inactive'}</span>
                  </div>
                  <button onClick={() => deactivateUser(u.id, u.name)} style={{ background: 'transparent', border: `1px solid ${C.red}44`, color: C.red, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit' }}>Deactivate</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function DashboardPage({ user, onLogout }) {
  const token = localStorage.getItem('token');
  const [active, setActive] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(window.innerWidth > 768);
  const [data, setData] = useState({
    property_address: '', postal_code: '', floor: '', apartment_number: '',
    purchase_date: '', purchase_price: '', lawyer_id: '',
    purchase_contract_name: '', certification_name: '', permit_cards_name: '',
    title_deed_name: '', passport_copies_name: '',
    permit_expiry_date: '', permit_renewal_date: '',
    accountant_id: '',
    tax_2023: '', tax_2024: '', tax_2025: '', tax_2026: '', tax_2027: '',
    enfia_2022: '', enfia_2023: '', enfia_2024: '', enfia_2025: '', enfia_2026: '', enfia_2027: '',
    electricity_meter: '', water_meter: '', gas_meter: '',
    building_manager: '', building_manager_phone: '', building_manager_email: '',
    current_tenant: '', tenant_phone: '', rental_expiry: '',
    rental_price: '', rental_duration: '', rental_contract_name: '',
    bank_name: '', bank_branch: '', iban: '', swift_code: '',
  });
  const [lawyers, setLawyers] = useState([]);
  const [accountants, setAccountants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const canEdit = ['owner', 'admin', 'manager'].includes(user.role);
  const ownerSections = ['property', 'legal', 'permit', 'accounting', 'bills', 'tenant', 'bank'];

  useEffect(() => {
    const stored = localStorage.getItem(`owner_data_${user.id}`);
    if (stored) { try { setData(JSON.parse(stored)); } catch {} }
  }, [user.id]);

  useEffect(() => {
    if (data.permit_expiry_date) {
      const d = new Date(data.permit_expiry_date);
      d.setMonth(d.getMonth() - 2);
      setData(prev => ({ ...prev, permit_renewal_date: d.toISOString().split('T')[0] }));
    }
  }, [data.permit_expiry_date]);

  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    fetch(`${API_URL}/users?role=lawyer`, { headers: h }).then(r => r.json()).then(d => setLawyers(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API_URL}/users?role=accountant`, { headers: h }).then(r => r.json()).then(d => setAccountants(Array.isArray(d) ? d : [])).catch(() => {});
  }, [token]);

  const save = () => {
    setSaving(true);
    localStorage.setItem(`owner_data_${user.id}`, JSON.stringify(data));
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000); }, 600);
  };

  const isOwnerSection = ownerSections.includes(active);

  const pageTitles = { dashboard: '◈ Dashboard', property: '🏛️ Property Details', legal: '⚖️ Legal Details', permit: '🛂 Residence Permit', accounting: '📊 Accounting', bills: '💡 Bills Details', tenant: '🏠 Tenant Info', bank: '🏦 Bank Details', tickets: '🎫 Tickets', users: '👥 User Management' };

  return (
    <div style={{ minHeight: '100vh', background: C.cream, fontFamily: 'Lato, sans-serif', display: 'flex', flexDirection: 'column' }}>
      <TopBar user={user} onLogout={onLogout} onMenuToggle={() => setMenuOpen(o => !o)} menuOpen={menuOpen} />
      <Sidebar active={active} onNavigate={setActive} role={user.role} isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      <div style={{ flex: 1, padding: '20px 20px', maxWidth: 800, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>

        {active !== 'dashboard' && active !== 'users' && active !== 'tickets' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
            <h2 style={{ fontFamily: 'Georgia, serif', color: C.navy, fontSize: 20, margin: 0 }}>{pageTitles[active]}</h2>
            {canEdit && isOwnerSection && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {saved && <span style={{ color: C.green, fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
                <button style={S.btn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            )}
          </div>
        )}

        {active === 'dashboard' && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', color: C.navy, fontSize: 24, margin: '0 0 4px' }}>Welcome, {user.name.split(' ')[0]}!</h1>
            <p style={{ color: C.slate, fontSize: 13, margin: '0 0 24px' }}>SOLVEURE Property Management Platform</p>

            {/* Open Ticket button prominently on dashboard */}
            <div style={{ background: C.navy, borderRadius: 12, padding: 20, marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, color: C.gold, fontWeight: 700 }}>Need Help?</div>
                <div style={{ fontSize: 13, color: '#ffffff88', marginTop: 4 }}>Open a support ticket and our team will respond promptly.</div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setActive('tickets')} style={{ background: C.lgray, color: C.navy, border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit' }}>
                  🎫 My Tickets
                </button>
                <button onClick={() => { setActive('tickets'); }} style={S.btnGold}>
                  + Open Ticket
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 24 }}>
              {[
                { label: 'Your Role', value: user.role?.toUpperCase(), color: ROLE_CONFIG[user.role]?.color || C.gold },
                { label: 'Platform', value: 'ACTIVE', color: C.green },
                { label: 'API', value: 'CONNECTED', color: C.green },
                { label: 'Database', value: 'ONLINE', color: C.green },
              ].map(s => (
                <div key={s.label} style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 14px' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontFamily: 'Georgia, serif' }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: C.navy, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {['owner', 'admin', 'manager'].includes(user.role) && (
              <div style={{ background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                <div style={{ fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 14 }}>Quick Access</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {[
                    { key: 'property', icon: '🏛️', label: 'Property' },
                    { key: 'legal', icon: '⚖️', label: 'Legal' },
                    { key: 'permit', icon: '🛂', label: 'Permit' },
                    { key: 'accounting', icon: '📊', label: 'Accounting' },
                    { key: 'bills', icon: '💡', label: 'Bills' },
                    { key: 'tenant', icon: '🏠', label: 'Tenant' },
                    { key: 'bank', icon: '🏦', label: 'Bank' },
                    { key: 'tickets', icon: '🎫', label: 'Tickets' },
                    ...(user.role === 'admin' ? [{ key: 'users', icon: '👥', label: 'Users' }] : []),
                  ].map(m => (
                    <button key={m.key} onClick={() => setActive(m.key)} style={{ background: C.lgray, border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 10px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                      <span style={{ fontSize: 20 }}>{m.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{m.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isOwnerSection && (
          <>
            <OwnerSections active={active} data={data} setData={setData} canEdit={canEdit} lawyers={lawyers} accountants={accountants} />
            {canEdit && (
              <div style={{ textAlign: 'right', marginTop: 4, paddingBottom: 32 }}>
                {saved && <span style={{ color: C.green, fontSize: 13, fontWeight: 600, marginRight: 12 }}>✓ Saved</span>}
                <button style={S.btn} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            )}
          </>
        )}

        {active === 'tickets' && <TicketsPanel user={user} token={token} />}
        {active === 'users' && <UserManagementPanel token={token} />}
      </div>
    </div>
  );
}

// ── Login ─────────────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verified = searchParams.get('verified') === 'true';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  const login = async () => {
    setLoading(true); setError(''); setUnverified(false);
    try {
      const res = await fetch(`${API_URL}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) { if (data.code === 'EMAIL_NOT_VERIFIED') setUnverified(true); setError(data.error || 'Invalid email or password.'); }
      else { localStorage.setItem('token', data.accessToken); localStorage.setItem('user', JSON.stringify(data.user)); onLogin(data.user); navigate('/dashboard'); }
    } catch { setError('Could not connect to server.'); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    try { await fetch(`${API_URL}/auth/resend-verification`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }); setResendMsg('New verification link sent.'); }
    catch { setResendMsg('Could not resend.'); }
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.card, maxWidth: 420 }}>
        <Logo />
        {verified && <div style={S.success}>✓ Email verified! You can now log in.</div>}
        {error && <div style={S.error}>{error}{unverified && <div style={{ marginTop: 8 }}><button style={{ ...S.link, color: C.red, fontSize: 12 }} onClick={resend}>Resend verification email</button></div>}</div>}
        {resendMsg && <div style={S.success}>{resendMsg}</div>}
        <label style={S.label}>EMAIL ADDRESS</label>
        <input style={S.input} type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} />
        <label style={S.label}>PASSWORD</label>
        <input style={S.input} type="password" placeholder="Your password" value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && login()} />
        <button style={S.btnFull} onClick={login} disabled={loading}>{loading ? 'Signing in...' : 'Sign In →'}</button>
      </div>
    </div>
  );
}

// ── App Root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));
  const handleLogin = u => { setUser(u); localStorage.setItem('user', JSON.stringify(u)); };
  const handleLogout = () => setUser(null);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/dashboard" element={user ? <DashboardPage user={user} onLogout={handleLogout} /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/login" element={user ? <DashboardPage user={user} onLogout={handleLogout} /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="*" element={user ? <DashboardPage user={user} onLogout={handleLogout} /> : <LoginPage onLogin={handleLogin} />} />
      </Routes>
    </BrowserRouter>
  );
}
